// === SAA:BRANDING:BRAND_SELECTOR ===
// SAA-exclusive brand switcher (NU91, DFD, SPD) — LOCAL DEV ONLY (gated on import.meta.env.DEV);
// it never renders on a deployed build. Deployments still switch brand via the ?brand= URL param /
// subdomain (core brandDetection); this button is just a dev convenience.
// Renders in the Header actions area via HeaderExtrasProvider.
// "default" brand is treated as NU91 for display purposes.
// Shows a confirmation popover when switching mid-workflow to prevent accidental data loss.

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfig, getCurrentBrand, getSupportedBrands, suppressLeaveWarningOnce, BrandId } from '@sluitstraat/core';

const SESSION_STORAGE_KEY = 'sluitstraat_brand';

const BRAND_LABELS: Record<BrandId, string> = {
  nu91: 'NU91',
  dfd: 'DFD',
  spd: 'SPD',
  default: 'NU91',
};

// Favicon paths per brand (served from /public)
const BRAND_ICONS: Record<BrandId, string> = {
  nu91: '/favicon.ico',
  dfd: '/favicon-dfd.ico',
  spd: '/favicon-spd.ico',
  default: '/favicon.ico',
};

/** Resolve "default" to "nu91" for display and switching purposes */
function resolveDisplayBrand(brand: BrandId): BrandId {
  return brand === 'default' ? 'nu91' : brand;
}

/** Check if the user is past the first workflow page (product-selection) */
function isDeepInWorkflow(pathname: string): boolean {
  return pathname.startsWith('/workflow/') && pathname !== '/workflow/product-selection';
}

export const BrandSelector: React.FC = () => {
  const { config } = useConfig();
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<BrandId | null>(null);

  if (!import.meta.env.DEV) return null;
  if (!config?.branding?.showBrandSelector) return null;

  const rawBrand = getCurrentBrand();
  const displayBrand = resolveDisplayBrand(rawBrand);
  const brands = getSupportedBrands();

  const dismissAll = () => {
    setIsOpen(false);
    setConfirmTarget(null);
  };

  const executeBrandSwitch = (brand: BrandId) => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, brand);
    } catch (_) { /* ignore */ }

    const url = new URL(window.location.href);
    url.searchParams.set('brand', brand);
    if (isDeepInWorkflow(location.pathname)) {
      url.pathname = '/workflow/product-selection';
    }
    window.location.href = url.toString();
  };

  const handleBrandSwitch = (brand: BrandId) => {
    if (brand === displayBrand) {
      setIsOpen(false);
      return;
    }

    // If user is deep in the workflow, show confirmation popover
    if (isDeepInWorkflow(location.pathname)) {
      setIsOpen(false);
      setConfirmTarget(brand);
      return;
    }

    executeBrandSwitch(brand);
  };

  const handleConfirm = () => {
    if (confirmTarget) {
      suppressLeaveWarningOnce();
      executeBrandSwitch(confirmTarget);
    }
  };

  return (
    <div style={styles.container}>
      <button
        onClick={() => {
          setConfirmTarget(null);
          setIsOpen(!isOpen);
        }}
        style={styles.button}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f5f5f5';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'white';
        }}
        aria-label="Select brand"
        title={`Brand: ${BRAND_LABELS[displayBrand]}`}
      >
        <img
          src={BRAND_ICONS[displayBrand]}
          alt={BRAND_LABELS[displayBrand]}
          style={styles.brandIcon}
        />
        <span style={styles.brandCode}>{BRAND_LABELS[displayBrand]}</span>
        <span style={styles.arrow}>{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>

      {/* Brand dropdown */}
      {isOpen && (
        <>
          <div style={styles.backdrop} onClick={dismissAll} />
          <div style={styles.dropdown}>
            {brands.map((brand) => (
              <button
                key={brand}
                onClick={() => handleBrandSwitch(brand)}
                style={{
                  ...styles.option,
                  ...(brand === displayBrand ? styles.optionActive : {}),
                }}
                onMouseEnter={(e) => {
                  if (brand !== displayBrand) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (brand !== displayBrand) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <img
                  src={BRAND_ICONS[brand]}
                  alt={BRAND_LABELS[brand]}
                  style={styles.brandIcon}
                />
                <span style={styles.optionName}>{BRAND_LABELS[brand]}</span>
                {brand === displayBrand && <span style={styles.checkmark}>{'\u2713'}</span>}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Confirmation popover when switching mid-workflow */}
      {confirmTarget && (
        <>
          <div style={styles.backdrop} onClick={dismissAll} />
          <div style={styles.confirmPopover}>
            <p style={styles.confirmText}>
              {t('brandSelector.confirmSwitch')}
            </p>
            <div style={styles.confirmActions}>
              <button
                onClick={dismissAll}
                style={styles.cancelButton}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e5e7eb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
              >
                {t('brandSelector.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                style={styles.confirmButton}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                {t('brandSelector.confirm')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 18px',
    backgroundColor: 'white',
    border: '1.5px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  brandIcon: {
    width: '16px',
    height: '16px',
    objectFit: 'contain' as const,
    flexShrink: 0,
  },
  brandCode: {
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    color: '#1a1a1a',
  },
  arrow: {
    fontSize: '10px',
    opacity: 0.5,
    marginLeft: '2px',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    left: 0,
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1000,
    overflow: 'hidden',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    textAlign: 'left' as const,
    transition: 'background-color 0.2s ease',
  },
  optionActive: {
    backgroundColor: '#e3f2fd',
    color: 'var(--primary-color, #1976d2)',
  },
  optionName: {
    flex: 1,
  },
  checkmark: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: 'var(--primary-color, #1976d2)',
  },
  // Confirmation popover (appears below the button, same position as dropdown)
  confirmPopover: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    padding: '14px 16px',
    width: '260px',
    zIndex: 1000,
  },
  confirmText: {
    margin: '0 0 12px 0',
    fontSize: '13px',
    color: '#4b5563',
    lineHeight: '1.5',
  },
  confirmActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '6px 14px',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    transition: 'background-color 0.2s ease',
  },
  confirmButton: {
    padding: '6px 14px',
    backgroundColor: 'var(--primary-color, #056666)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: 'white',
    transition: 'opacity 0.2s ease',
  },
};

export default BrandSelector;
